import { css, html, LitElement, type TemplateResult } from 'lit';
import { customElement, property, query, queryAssignedElements } from 'lit/decorators.js';

/**
 * A wrapper element to display a speech-bubble-style popover.
 * Slotted elements with attribute 'data-popover' will trigger the popover on hover.
 * The content of the popover is set by the value of 'data-popover'.
 * The bubble is white by default, but can be styled with classes 'blue', 'green' or 'orange' set on element,
 * or by setting the same keywords in attribute 'data-popover-background' on trigger elements.
 */
@customElement('d-popover-bubble')
export class DPopoverBubble extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }

    #wrapper {
      position: fixed;
      inset: 0;
      margin: auto;
      width: 100vw;
      height: 0;
      border: none;
      padding: 0;
      overflow: visible;
      font-size: 15px;
      line-height: normal;
    }

    #positioner {
      display: flex;
      justify-content: center;
      position: absolute;
      width: 100vw;
      box-sizing: border-box;
      text-align: center;
      filter: drop-shadow(0 2px 10px hsla(1, 0%, 0%, 0.3));
    }

    :host(.blue) #positioner,
    :host(.green) #positioner,
    :host(.orange) #positioner {
      filter: none;
    }

    #bubble {
      display: inline-block;
      max-width: 300px;
      border-radius: 6px;
      background: white;
      padding: 8px 12px;
      color: var(--text-color);
    }

    :host(.blue) #bubble {
      background: var(--themeColor);
      color: white;
    }

    :host(.green) #bubble {
      background: #03c703;
      color: white;
    }

    :host(.orange) #bubble {
      background: var(--alertColor);
      color: white;
    }

    #positioner:after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      margin-left: -8px;
      border-width: 10px 8px 0 8px;
      border-style: solid;
      border-color: white transparent transparent transparent;
    }

    :host(.blue) #positioner:after {
      border-top-color: var(--themeColor);
    }

    :host(.green) #positioner:after {
      border-top-color: #03c703;
    }

    :host(.orange) #positioner:after {
      border-top-color: var(--alertColor);
    }
  `;

  /**
   * Distance from the top center of the trigger element to the tail of the bubble
   */
  @property({ type: Number })
  offset = 4;
  @query('#wrapper')
  wrapper!: HTMLElement;
  @query('#positioner')
  positioner!: HTMLElement;
  @query('#bubble')
  bubble!: HTMLElement;
  @queryAssignedElements()
  slottedElements!: Array<HTMLElement>;

  private tailHeight = 10;

  onMouseOver(e) {
    const trigger = e.target.closest('*[data-popover]');
    if (trigger && trigger.dataset.popover !== '' && 'showPopover' in this.wrapper) {
      const rect = trigger.getBoundingClientRect();
      const left = (window.innerWidth / 2) * -1 + rect.left + rect.width / 2;
      const bottom = window.innerHeight / 2 + this.tailHeight + this.offset - rect.top;
      this.positioner.style.left = Math.round(left) + 'px';
      this.positioner.style.bottom = Math.round(bottom) + 'px';
      this.bubble.textContent = trigger.dataset.popover ?? '';
      if (trigger.dataset.popoverBackground) {
        this.classList.add(trigger.dataset.popoverBackground);
      }
      this.wrapper.showPopover();
    }
  }

  onMouseOut(e) {
    const trigger = e.target.closest('*[data-popover]');
    if (trigger && trigger.dataset.popover !== '' && 'hidePopover' in this.wrapper) {
      this.wrapper.hidePopover();
      if (trigger.dataset.popoverBackground) {
        this.classList.remove(trigger.dataset.popoverBackground);
      }
    }
  }

  render(): TemplateResult<1> {
    return html`
      <slot @mouseover=${(e) => this.onMouseOver(e)} @mouseout=${(e) => this.onMouseOut(e)}></slot>
      <div popover="manual" id="wrapper">
        <div id="positioner">
          <div id="bubble"></div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-popover-bubble': DPopoverBubble;
  }
}
